// libs/utils/src/plans.ts
export type SubscriptionTier = "FREE" | "PRO" | "ULTIMATE";

export type FeatureKind = "quota" | "boolean";

export type FeatureDefinition = {
  /** Stable key referenced by code. */
  kind: FeatureKind;
  /** Display label used by pricing UI and paywall copy. */
  label: string;
};

/**
 * Every capability the product gates on, named once.
 * `kind: "quota"` features are numeric (limits per total/month/day/hour).
 * `kind: "boolean"` features are pure entitlements (on/off per tier).
 */
export const FEATURES = {
  resumes: { kind: "quota", label: "Resumes" },
  jobProfiles: { kind: "quota", label: "Job profiles" },
  aiTailoring: { kind: "quota", label: "AI resume tailoring" },
  autoApplications: { kind: "quota", label: "Tailored applications" },
  atsScoreChecker: { kind: "quota", label: "ATS score checker" },
  resumeRoast: { kind: "quota", label: "Resume roast" },
  endorsementWriter: { kind: "quota", label: "LinkedIn endorsement writer" },
  linkedinPreview: { kind: "quota", label: "LinkedIn profile preview" },
  linkedinReview: { kind: "quota", label: "LinkedIn profile review" },
  resumeParse: { kind: "quota", label: "Resume parse uploads" },

  publicShareLink: { kind: "boolean", label: "Public shareable resume link" },
  applicationTracker: { kind: "boolean", label: "Application tracker (Kanban)" },
  gmailSignals: { kind: "boolean", label: "Gmail inbox signals" },
  advancedAnalytics: { kind: "boolean", label: "Advanced analytics dashboard" },
  premiumTemplates: { kind: "boolean", label: "Premium resume templates" },
} as const satisfies Record<string, FeatureDefinition>;

export type FeatureKey = keyof typeof FEATURES;

/** Sentinel: -1 means unlimited; 0 means blocked. */
export type Quota = {
  total?: number;
  perMonth?: number;
  perDay?: number;
  perHour?: number;
};

/**
 * Flattened, per-tier usage-limit object written to the `UsageLimit` row on
 * subscription events. Mirrors the fields that `BillingService.getUsageLimitsForTier`
 * computes from the PLANS quotas map.
 */
export type TierLimits = {
  resumesLimit: number;
  aiGenerationsLimit: number;
  applicationsLimit: number;
  applicationsPerDayLimit: number;
  autoApplyBurstPerHour: number;
  aiTailoringsPerDayLimit: number;
  jobProfilesLimit: number;
  linkedinPreviewsLimit: number;
  linkedinReviewsLimit: number;
  // Spec 033 — paid-spend caps in USD cents. Zero on FREE blocks every
  // paid AI / proxy call. PRO + ULTIMATE values are tuned against
  // research.md R1 observed usage; flip via tier change to take effect.
  aiCapCentsPerDay: number;
  aiCapCentsPerMonth: number;
  proxyCapCentsPerDay: number;
  proxyCapCentsPerMonth: number;
};

export type Plan = {
  name: string;
  /**
   * Display prices. PRO adds a weekly plan; ULTIMATE has no weekly at launch
   * (`weekly` omitted). Single source of truth for the pricing cards — no
   * scattered literals.
   *
   * `yearly` is the **annual total** (e.g. PRO $179/yr), NOT a per-month
   * equivalent — the pricing page divides by 12 for the "/mo billed yearly"
   * display. `savePct.yearly` is the rounded discount of the annual total vs
   * `monthly × 12`, surfaced as the "save N%" badge. It's stored (not derived
   * at render) so the badge is pinned by the contract test and copy stays
   * stable. Omitted on FREE (no paid annual plan).
   */
  price: { weekly?: number; monthly: number; yearly: number; savePct?: { yearly: number } };
  quotas: Partial<Record<FeatureKey, Quota>>;
  entitlements: Partial<Record<FeatureKey, boolean>>;
  /** Short bullets shown on the public pricing-page tier card. */
  marketingHighlights: readonly string[];
};

/**
 * Subscription tier → quota + entitlement map. Single source of truth.
 *
 * Sentinels:
 *   -1 = unlimited (skip cap check)
 *    0 = blocked  (feature unavailable on this tier)
 *
 * Auto-apply is metered as a monthly credit pool: `autoApplications.perMonth`
 * is the allotment (FREE 0 / PRO 150 / ULTIMATE 500), `perDay` is the hard
 * daily safety cap.
 */
export const PLANS = {
  FREE: {
    name: "Free",
    price: { monthly: 0, yearly: 0 },
    quotas: {
      resumes: { total: 2 },
      // 1 profile so free users can taste the job feed (top-N preview — see
      // FREE_FEED_PREVIEW_LIMIT). The feed itself stays the paid product.
      jobProfiles: { total: 1 },
      aiTailoring: { perDay: 0 },
      autoApplications: { perMonth: 0, perDay: 0, perHour: 0 },
      atsScoreChecker: { perDay: 3 },
      resumeRoast: { perDay: 1 },
      endorsementWriter: { perDay: 1 },
      linkedinPreview: { perMonth: 3 },
      linkedinReview: { perMonth: 0 },
      resumeParse: { perDay: 2 },
    },
    entitlements: {
      publicShareLink: false,
      applicationTracker: false,
      gmailSignals: false,
      advancedAnalytics: false,
      premiumTemplates: false,
    },
    marketingHighlights: [
      "2 resumes",
      "Job feed preview — 10 AI-scored matches",
      "12 free templates · PDF export",
      "ATS score checker — 3/day",
      "Resume roast — 1/day",
      "LinkedIn endorsement writer — 1/day",
      "PDF export",
    ],
  },
  PRO: {
    name: "Pro",
    // $7.99/wk · $19.99/mo · $179/yr (~25% off yearly).
    // savePct: 179 vs 19.99×12 (239.88) → 25% off.
    price: { weekly: 7.99, monthly: 19.99, yearly: 179, savePct: { yearly: 25 } },
    quotas: {
      resumes: { total: 10 },
      jobProfiles: { total: 3 },
      // perMonth is the real cost ceiling; perDay only shapes the burst.
      // At ~0.4c per tailoring on llama-3.3-70b-versatile, 150/mo is well
      // under $1 of Groq spend against $19.99 revenue — so a 5/day cap was
      // protecting nothing and actively cost us users. Our most engaged
      // trial user ever hit it 120 times in a single session (161 attempted
      // applications, all failed) and never returned. Raised to match
      // ULTIMATE's daily burst; the tiers still differ on monthly volume.
      aiTailoring: { perMonth: 150, perDay: 25 },
      autoApplications: { perMonth: 150, perDay: 5, perHour: 25 },
      // Same reasoning as aiTailoring above. Measured on the current model
      // (openai/gpt-oss-120b, 15c/60c per million) with real prod token
      // profiles: an ATS check costs ~0.035c and a roast ~0.055c, so 25/day
      // for a month is single-digit cents per user. Nobody has ever come
      // close to 5 — busiest day on record is 3 roasts and 1 ATS check —
      // so this is removing a tripwire before it costs us someone, not
      // responding to demand.
      atsScoreChecker: { perDay: 25 },
      resumeRoast: { perDay: 25 },
      endorsementWriter: { perDay: 25 },
      linkedinPreview: { perMonth: 15 },
      linkedinReview: { perMonth: 10 },
      resumeParse: { perDay: 10 },
    },
    entitlements: {
      publicShareLink: true,
      applicationTracker: true,
      gmailSignals: true,
      advancedAnalytics: false,
      premiumTemplates: true,
    },
    marketingHighlights: [
      "10 resumes",
      "All 18 templates (6 premium designs unlocked)",
      "3 job profiles",
      // Tiers now share the 25/day tailoring burst and differ on the monthly
      // pool, so lead with the number that actually differentiates.
      "AI tailoring — 150/month",
      "5 tailored applications per day (150 credits/month)",
      "ATS checks, roasts, endorsements — 25/day each",
      "Gmail inbox signals — recruiter replies surface as they land — BETA",
      "Public shareable resume link",
    ],
  },
  ULTIMATE: {
    name: "Ultimate",
    // $34.99/mo · $299/yr (~28% off yearly). No weekly at launch.
    // savePct: 299 vs 34.99×12 (419.88) → 28% off.
    price: { monthly: 34.99, yearly: 299, savePct: { yearly: 28 } },
    quotas: {
      resumes: { total: 20 },
      jobProfiles: { total: 10 },
      aiTailoring: { perMonth: 500, perDay: 25 },
      autoApplications: { perMonth: 500, perDay: 25, perHour: 50 },
      atsScoreChecker: { perDay: 25 },
      resumeRoast: { perDay: 25 },
      endorsementWriter: { perDay: 25 },
      linkedinPreview: { perMonth: 30 },
      linkedinReview: { perMonth: 30 },
      resumeParse: { perDay: 20 },
    },
    entitlements: {
      publicShareLink: true,
      applicationTracker: true,
      gmailSignals: true,
      advancedAnalytics: true,
      premiumTemplates: true,
    },
    marketingHighlights: [
      "20 resumes",
      "10 job profiles (3× Pro's cap)",
      "AI tailoring — 500/month",
      "25 tailored applications per day (500/month)",
      "ATS checks, roasts, endorsements — 25/day each",
      "LinkedIn profile review — 30/month",
      "Advanced analytics dashboard",
      "Priority email support",
      "Everything in Pro",
    ],
  },
} as const satisfies Record<SubscriptionTier, Plan>;

/**
 * FREE-tier job-feed preview: how many matches a free user can see. The rest
 * render as a locked upsell ("+N more matches — upgrade"). Shared between the
 * server clamp (JobSearchService.listJobs) and the client CTA copy so the two
 * can never disagree. Raising it gives away more of the paid product; the
 * scoring cost is negligible (Groq ~5¢/M tokens).
 */
export const FREE_FEED_PREVIEW_LIMIT = 10;

/**
 * Hard ceiling for "unlimited" (resumes total=-1) accounts. Defensive cap
 * to prevent a runaway bug or abuse from filling storage. Preserved from
 * the legacy module — distinct from the per-tier limit.
 */
export const RESUMES_ABSOLUTE_CAP = 100;

/** Anomaly thresholds for the weekly resume-volume audit job. */
export const RESUME_ANOMALY_TOTAL_THRESHOLD = 50;
export const RESUME_ANOMALY_RECENT_THRESHOLD = 10;
export const RESUME_ANOMALY_RECENT_WINDOW_DAYS = 7;

/**
 * Returns the Plan object for a tier. Always returns FREE for unknown
 * inputs — defensive default, since this is on the webhook hot path.
 */
export function getPlan(tier: string | null | undefined): Plan {
  if (tier === "PRO") return PLANS.PRO;
  if (tier === "ULTIMATE") return PLANS.ULTIMATE;
  return PLANS.FREE;
}

/**
 * Returns the quota object for `(tier, feature)`. Returns an empty
 * object `{}` if the feature is not a quota or isn't configured for the
 * tier — callers should treat missing windows as "no limit configured".
 */
export function getQuota(tier: string | null | undefined, feature: FeatureKey): Quota {
  const plan = getPlan(tier);
  return plan.quotas[feature] ?? {};
}

/**
 * Returns true if the tier has the boolean entitlement. Returns false
 * for unknown features or quota-kind features.
 */
export function hasEntitlement(tier: string | null | undefined, feature: FeatureKey): boolean {
  const plan = getPlan(tier);
  return plan.entitlements[feature] === true;
}

export type ComparisonCell = boolean | string;
export type ComparisonRow = {
  feature: string;
  free: ComparisonCell;
  pro: ComparisonCell;
  ultimate: ComparisonCell;
};
export type ComparisonGroup = {
  name: string;
  rows: readonly ComparisonRow[];
};

// Hoisted helpers so the same closures aren't recreated on every comparePlans()
// call. They depend only on PLANS, which is module-level static.
const quotaCell = (tier: SubscriptionTier, feature: FeatureKey): ComparisonCell => {
  const quota = (PLANS[tier] as Plan).quotas[feature];
  if (!quota) return false;
  if (quota.perDay === -1 || quota.perMonth === -1 || quota.total === -1) return "Unlimited";
  if (quota.perDay === 0 && quota.perMonth === 0) return false;
  if (quota.perDay !== undefined && quota.perMonth !== undefined && quota.perDay > 0) {
    return `${quota.perDay} / day · ${quota.perMonth} / mo`;
  }
  if (quota.perDay !== undefined) return quota.perDay === 0 ? false : `${quota.perDay} / day`;
  if (quota.perMonth !== undefined) return quota.perMonth === 0 ? false : `${quota.perMonth} / mo`;
  if (quota.total !== undefined) return `${quota.total}`;
  return false;
};
const entitlementCell = (tier: SubscriptionTier, feature: FeatureKey): ComparisonCell =>
  (PLANS[tier] as Plan).entitlements[feature] === true;
const buildRow = (
  feature: string,
  cells: [ComparisonCell, ComparisonCell, ComparisonCell],
): ComparisonRow => ({ feature, free: cells[0], pro: cells[1], ultimate: cells[2] });

/**
 * The structured side-by-side comparison rendered on the public pricing
 * page. Built from `PLANS` so the table can never drift from the
 * enforced limits.
 */
export function comparePlans(): readonly ComparisonGroup[] {
  const q = quotaCell;
  const e = entitlementCell;
  const row = buildRow;

  return [
    {
      name: "Resumes & exports",
      rows: [
        row("Resumes", [q("FREE", "resumes"), q("PRO", "resumes"), q("ULTIMATE", "resumes")]),
        row("Templates", ["12 free", "All 18", "All 18"]),
        row("Premium templates (6 designs)", [
          e("FREE", "premiumTemplates"),
          e("PRO", "premiumTemplates"),
          e("ULTIMATE", "premiumTemplates"),
        ]),
        row("Public shareable resume link", [
          e("FREE", "publicShareLink"),
          e("PRO", "publicShareLink"),
          e("ULTIMATE", "publicShareLink"),
        ]),
      ],
    },
    {
      name: "AI tools",
      rows: [
        row("ATS score checker", [
          q("FREE", "atsScoreChecker"),
          q("PRO", "atsScoreChecker"),
          q("ULTIMATE", "atsScoreChecker"),
        ]),
        row("Resume roast", [
          q("FREE", "resumeRoast"),
          q("PRO", "resumeRoast"),
          q("ULTIMATE", "resumeRoast"),
        ]),
        row("LinkedIn endorsement writer", [
          q("FREE", "endorsementWriter"),
          q("PRO", "endorsementWriter"),
          q("ULTIMATE", "endorsementWriter"),
        ]),
        row("LinkedIn profile review", [
          q("FREE", "linkedinReview"),
          q("PRO", "linkedinReview"),
          q("ULTIMATE", "linkedinReview"),
        ]),
        row("Resume tailoring (AI)", [
          q("FREE", "aiTailoring"),
          q("PRO", "aiTailoring"),
          q("ULTIMATE", "aiTailoring"),
        ]),
      ],
    },
    {
      name: "Job search & Apply Assist",
      rows: [
        row("Job profiles", [
          q("FREE", "jobProfiles"),
          q("PRO", "jobProfiles"),
          q("ULTIMATE", "jobProfiles"),
        ]),
        row("AI-scored job feed", [
          `${FREE_FEED_PREVIEW_LIMIT}-match preview`,
          "Full feed",
          "Full feed",
        ]),
        row("Tailored applications", [
          q("FREE", "autoApplications"),
          q("PRO", "autoApplications"),
          q("ULTIMATE", "autoApplications"),
        ]),
        row("Gmail inbox signals", [
          e("FREE", "gmailSignals"),
          e("PRO", "gmailSignals"),
          e("ULTIMATE", "gmailSignals"),
        ]),
      ],
    },
    {
      name: "Analytics & support",
      rows: [
        row("Advanced analytics dashboard", [
          e("FREE", "advancedAnalytics"),
          e("PRO", "advancedAnalytics"),
          e("ULTIMATE", "advancedAnalytics"),
        ]),
        row("Email support", ["Community", "Standard", "Priority"]),
      ],
    },
  ] as const;
}
